import { MAIN_CONTENT_PADDING } from './contentPadding'
import { useEffect, useRef, useState } from 'react'
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom'
import { CrossTabIdentityBanner } from './CrossTabIdentityBanner'
import { useCrossTabIdentity } from '@/hooks/useCrossTabIdentity'
import { PersonaSidebar } from './PersonaSidebar'
import { Header } from './Header'
import { RouteErrorBoundary } from './RouteErrorBoundary'
import { PageTransition } from './PageTransition'
import { CommandPalette } from '@/components/command-palette/CommandPalette'
import { useCommandPalette } from '@/components/command-palette/useCommandPalette'
import { useTenantPageTitle } from '@/hooks/useTenantPageTitle'
import { useIdleTimeout } from '@/hooks/useIdleTimeout'
import { IdleWarning } from '@/features/auth/components/IdleWarning'
import { cn } from '@/utils/cn'
import { subscribeAnnouncements } from '@/lib/announce'
import { useAuthStore } from '@/stores/authStore'
import { TourProvider, TourLauncher } from '@/features/product-tour'
import { AgentUpgradeTenantBanner } from '@/features/clusters/components/AgentUpgradeTenantBanner'
import { useGuidedToursPreference } from '@/features/settings/hooks/useGuidedToursPreference'

export function Layout() {
  useTenantPageTitle()
  // Log out after inactivity (VITE_IDLE_TIMEOUT_MINUTES, default 30). Mounted
  // once here in the authenticated shell so it covers every signed-in route.
  useIdleTimeout()
  // Register global Cmd+K / Ctrl+K listener (replaces the old AppShell sibling;
  // data router requires router hooks to be called inside RouterProvider tree).
  useCommandPalette()
  const location = useLocation()
  const identityKey = useAuthStore((s) => `${s.user?.tenantId ?? ''}:${s.user?.id ?? ''}`)
  const crossTabChange = useCrossTabIdentity()
  const tenantName = useAuthStore((s) => s.user?.tenantName)
  const [orgAnnouncement, setOrgAnnouncement] = useState('')
  const lastTenantName = useRef(tenantName)
  useEffect(() => {
    if (tenantName && tenantName !== lastTenantName.current) setOrgAnnouncement(`Now in ${tenantName}`)
    lastTenantName.current = tenantName
  }, [tenantName])
  // Pages announce through the same live region (lib/announce), e.g. an approval decision.
  useEffect(() => subscribeAnnouncements(setOrgAnnouncement), [])
  // Chat lives at /tenant/ai/chat (in-hub) since the AI-lands-on-Chat change;
  // the old /tenant/chat prefix is kept for the redirect's first paint.
  const isChatRoute =
    location.pathname.startsWith('/tenant/chat') ||
    location.pathname.startsWith('/tenant/ai/chat')
  // Animation scope key: the HUB ROOT, not the full pathname (UX review
  // 2026-08-13). Keying on the full path re-ran the cross-fade on every
  // sub-tab click inside a hub — a frequent, low-context-shift interaction an
  // operator performs dozens of times per session — adding perceived latency
  // where it should feel instant. Taking the first three segments
  // (/tenant/build/workflows → /tenant/build) means sibling tabs under one hub
  // do not remount or re-fade, while moving between hubs still animates.
  // slice(0,3) — NOT 4: the 4th segment IS the sub-tab/record id, so keeping it
  // would still re-fade every sub-tab click and, worse, remount the whole chat
  // page on every session switch (/tenant/chat/:sessionId), tearing down an
  // in-flight SSE stream and resetting scroll.
  const sectionKey = location.pathname.split('/').slice(0, 3).join('/')
  // Temporary kill-switch (dev): VITE_DISABLE_PRODUCT_TOUR=true hides the tour
  // launcher button alongside the suppressed auto-start in TourProvider.
  const tourEnvDisabled = import.meta.env.VITE_DISABLE_PRODUCT_TOUR === 'true'
  // C fix: also honour the durable server-side preference. Keep the launcher
  // visible while the preference is still loading (undefined) to avoid flicker;
  // hide only on an explicit false so tours-ON users see it immediately.
  const { showGuidedTours } = useGuidedToursPreference()
  const showLauncher = !tourEnvDisabled && showGuidedTours !== false

  return (
    <TourProvider>
      {/* Beskar floating window (kit §frame): on md+ the whole app shell sits as a
          rounded, bordered, shadowed "window" on the ground gradient painted by
          <body>. Pure presentation — the inner flex/scroll structure (and all of
          its documented overflow behavior) is unchanged; mobile stays full-bleed.
          NO overflow-hidden here — absolutely-positioned in-subtree dropdowns
          (PersonaSidebar's tenant-switcher, SearchBar's suggestions panel)
          would be clipped on short viewports (the same bug the <main> comment
          below documents). (Header's user menu and NotificationPanel USED to
          be the examples here, but they now portal to document.body via Radix
          Popover and can no longer be clipped by any ancestor.) The
          rounded corners are instead matched on the window's children:
          sidebar md:rounded-l-2xl, header md:rounded-tr-2xl, main
          md:rounded-br-2xl, so nothing pokes past the curve and nothing clips. */}
      <div className="h-full md:p-4">
      <div
        className="flex h-full bg-surface-0 md:rounded-2xl md:border md:border-[var(--glass-border)] md:shadow-xl"
        data-tour-id="welcome"
      >
        <PersonaSidebar />
        {/* min-h-0 (not overflow-hidden) — overflow-hidden clips absolutely-positioned
            in-subtree dropdowns (PersonaSidebar tenant-switcher, SearchBar
            suggestions; historically also NotificationPanel, which now portals
            to body and is immune). `<main>` below has its own
            overflow so the column still shrinks correctly under a flex parent.
            min-w-0: without it a flex child defaults to min-width:auto and refuses to
            shrink below its content's intrinsic width, so a wide page (table, canvas,
            whitespace-nowrap row) pushed this column past the viewport with the overflow
            clipped and NO horizontal scrollbar — the "runs off the right, must zoom out"
            bug. With min-w-0 the column shrinks and `<main>`'s overflow-x takes over. */}
        <div className="flex flex-1 flex-col min-h-0 min-w-0">
          <Header />
          {crossTabChange && crossTabChange.kind !== 'user' && <CrossTabIdentityBanner change={crossTabChange} />}
          {/* Agent upgrade status (Phase 1): owners/admins only, below-minimum only. */}
          <AgentUpgradeTenantBanner />
          {/* Persistent live region: announces the organization after a switch
              (screen readers otherwise get no cue that the context changed). */}
          <div className="sr-only" role="status" aria-live="polite" data-testid="org-announcer">
            {orgAnnouncement}
          </div>
          {/* bg-surface-0: paint the scroll region with the theme surface so a page
              shorter than the viewport shows themed surface, not the raw body
              background as a stark "black void". This is the whole fix for the short-
              content void — do NOT wrap <Outlet/> in a min-h-full div: min-height:100%
              is not a "definite" height, so it breaks h-full children (chat message
              pane, search/library/catalog/marketplace split panes) which then lose
              their internal scroll and get clipped by main's overflow-hidden.
              No overflow-hidden here either: it would clip absolutely-positioned
              in-subtree dropdowns (see comment above — sidebar tenant-switcher,
              search suggestions; NotificationPanel now portals and is immune);
              document-level scroll is contained via overflow-hidden on html/body in
              globals.css instead.
              overflow-auto (both axes), NOT overflow-y-auto: html/body are
              overflow-hidden (globals.css), so `<main>` is the ONLY scroll container —
              if it only scrolled Y, any content wider than the viewport was clipped with
              no way to reach it. overflow-auto lets a too-wide page scroll horizontally
              INSIDE the content area. Only shows an x-scrollbar when content actually
              overflows, so pages that fit are unaffected. */}
          <main className={cn('flex-1 overflow-hidden bg-surface-0 md:rounded-br-2xl', !isChatRoute && cn('overflow-auto', MAIN_CONTENT_PADDING))}>
            {/* key on tenantId:userId (#301): a tenant/user change remounts the
                whole route subtree, which closes every stream/socket/effect
                opened under the previous identity and drops component state
                (selected rows, open forms) that belongs to it. */}
            <RouteErrorBoundary key={identityKey}>
              {/* Route transition: PageTransition (framer-motion fade-in-up,
                  already reduced-motion aware) existed but was never wired up.
                  Keying on the full pathname replays the entrance on EVERY
                  navigation — top-level sections and hub sub-tabs alike, since
                  both change the pathname. Transform/opacity only, so it cannot
                  thrash layout; the key remount is cheap because route elements
                  are already remounted by the router on path change. */}
              {/* h-full is REQUIRED, not decorative: the comment above documents
                  that an intermediate wrapper without a definite height breaks
                  h-full children (chat message pane, search/library/catalog
                  split panes). h-full keeps that chain intact. */}
              <PageTransition key={sectionKey} className="h-full">
                <Outlet />
              </PageTransition>
            </RouteErrorBoundary>
          </main>
        </div>
        <CommandPalette />
        {/* Warns a minute before the server's idle sign-out (server idleTimeout). */}
        <IdleWarning />
        <ScrollRestoration />
        {showLauncher && <TourLauncher />}
      </div>
      </div>
    </TourProvider>
  )
}
